import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  LockKeyhole,
  Mail,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import "./LoginPage.css";

export default function DealerInvite() {
  const { token } = useParams();

  const [invitation, setInvitation] = useState(null);
  const [pageError, setPageError] = useState("");
  const [pageLoading, setPageLoading] = useState(true);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      try {
        const { data } = await axios.get(`/api/dealer-invitations/${token}`);
        setInvitation(data);
      } catch (err) {
        setPageError(err.response?.data?.error || "Invalid dealer invite link");
      } finally {
        setPageLoading(false);
      }
    };
    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (username.trim().length < 3) {
      setFormError("Username must be at least 3 characters");
      return;
    }

    if (password !== confirmPassword) {
      setFormError("Passwords do not match");
      return;
    }

    if (password.length < 4) {
      setFormError("Password must be at least 4 characters");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`/api/dealer-invitations/${token}/accept`, {
        username: username.trim(),
        password,
      });
      setSuccess(true);
    } catch (err) {
      setFormError(err.response?.data?.error || "Failed to create dealer account");
    } finally {
      setSubmitting(false);
    }
  };

  if (pageLoading) {
    return <InviteStatus title="Validating invitation" message="Checking your dealer setup link." loading />;
  }

  if (pageError) {
    return (
      <InviteStatus
        tone="error"
        title="Invitation invalid"
        message={pageError}
      />
    );
  }

  if (success) {
    return (
      <InviteStatus
        tone="success"
        title="Dealer account created"
        message="Your dealer account is ready. You can now open the mobile app on your phone and log in from Employee login."
      />
    );
  }

  return (
    <InvitePageShell>
      <section className="nexus-login__shell nexus-invite__shell" aria-label="Set up dealer mobile account">
        <div className="nexus-login__card">
          <InviteBrand />

          <div className="nexus-login__card-head">
            <p className="nexus-login__eyebrow">Dealer mobile setup</p>
            <h1>Create your dealer login</h1>
            <p className="nexus-invite__intro">
              You have been invited to join{" "}
              <strong>{invitation.cardroom}</strong> on Nexus Mobile.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="nexus-login__form">
            <InviteField label="Email" icon={<Mail size={18} aria-hidden="true" />}>
              <input type="email" value={invitation.email} readOnly />
            </InviteField>

            <InviteField label="Dealer" icon={<UserRound size={18} aria-hidden="true" />}>
              <input value={invitation.dealerName} readOnly />
            </InviteField>

            <InviteField label="Username" icon={<UserRound size={18} aria-hidden="true" />}>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                placeholder="Choose a username"
                autoComplete="username"
              />
            </InviteField>

            <InviteField label="Password" icon={<LockKeyhole size={18} aria-hidden="true" />}>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Create a password"
                autoComplete="new-password"
              />
            </InviteField>

            <InviteField label="Confirm Password" icon={<LockKeyhole size={18} aria-hidden="true" />}>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Repeat your password"
                autoComplete="new-password"
              />
            </InviteField>

            {formError && <div className="nexus-login__error">{formError}</div>}

            <button type="submit" disabled={submitting} className="nexus-login__submit">
              {submitting ? "Creating dealer account..." : "Create Dealer Account"}
            </button>
          </form>
        </div>
      </section>
    </InvitePageShell>
  );
}

function InvitePageShell({ children }) {
  return (
    <main className="nexus-login nexus-invite">
      <div className="nexus-login__ambient" aria-hidden="true">
        <div className="nexus-login__matrix">
          {Array.from({ length: 42 }).map((_, index) => (
            <span key={index} />
          ))}
        </div>
      </div>

      <Link className="nexus-login__back" to="/">
        <ArrowLeft size={17} aria-hidden="true" />
        Nexus Game Engine
      </Link>

      {children}
    </main>
  );
}

function InviteBrand() {
  return (
    <div className="nexus-login__brand nexus-login__brand--card">
      <span>N</span>
      <div>
        <strong>Nexus Game Engine</strong>
        <small>Dealer mobile access</small>
      </div>
    </div>
  );
}

function InviteField({ label, icon, children }) {
  return (
    <label className="nexus-login__field">
      <span>{label}</span>
      <div>
        {icon}
        {children}
      </div>
    </label>
  );
}

function InviteStatus({ tone = "neutral", title, message, loading = false }) {
  const Icon = tone === "error" ? AlertTriangle : CheckCircle2;

  return (
    <InvitePageShell>
      <section className="nexus-login__shell nexus-invite__shell" aria-label={title}>
        <div className="nexus-login__card nexus-invite__status">
          <InviteBrand />
          <div className={`nexus-invite__status-icon nexus-invite__status-icon--${tone}`}>
            {loading ? <ShieldCheck size={34} aria-hidden="true" /> : <Icon size={34} aria-hidden="true" />}
          </div>
          <p className="nexus-login__eyebrow">Dealer mobile setup</p>
          <h1>{title}</h1>
          <p>{message}</p>
        </div>
      </section>
    </InvitePageShell>
  );
}
